import React from 'react'
import { AlertProps as AntAlertProps } from 'antd'
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { Icon } from '../../Icon/Icon'
import { StyledAlert } from './styled'

export type AlertType =
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral'
  | 'light'
  | 'dark'
  | 'bordered'

export type AlertSize = 'small' | 'medium' | 'large'

export interface CustomAlertProps extends Omit<AntAlertProps, 'type' | 'size'> {
  type?: AlertType
  size?: AlertSize
  closable?: boolean
  onClose?: () => void
  showIcon?: boolean
  customIcon?: React.ReactNode
  title?: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
  rounded?: boolean
  fullWidth?: boolean
  children?: React.ReactNode
}

const AlertIcon: React.FC<{
  type: AlertType
  customIcon?: React.ReactNode
}> = ({ type, customIcon }) => {
  if (customIcon) {
    return <>{customIcon}</>
  }

  switch (type) {
    case 'success':
      return <CheckCircleOutlined />
    case 'warning':
      return <ExclamationCircleOutlined />
    case 'error':
      return <Icon name="error_alert" size={16} />
    case 'info':
    default:
      return <Icon name="info" size={16} />
  }
}

export const Alert: React.FC<CustomAlertProps> = ({
  type = 'info',
  size = 'medium',
  closable = false,
  onClose,
  showIcon = true,
  customIcon,
  title,
  description,
  message,
  actions,
  rounded = true,
  fullWidth = false,
  children,
  ...props
}) => {
  const alertMessage = message || title

  return (
    <StyledAlert
      type={type}
      size={size}
      closable={closable}
      onClose={onClose}
      showIcon={showIcon}
      icon={
        showIcon ? <AlertIcon type={type} customIcon={customIcon} /> : undefined
      }
      message={alertMessage}
      description={
        <>
          {description}
          {children}
          {actions && <div className="alert-actions">{actions}</div>}
        </>
      }
      rounded={rounded}
      fullWidth={fullWidth}
      {...props}
    />
  )
}

export const InfoAlert: React.FC<Omit<CustomAlertProps, 'type'>> = (props) => (
  <Alert type="info" {...props} />
)

export const SuccessAlert: React.FC<Omit<CustomAlertProps, 'type'>> = (
  props
) => <Alert type="success" {...props} />

export const WarningAlert: React.FC<Omit<CustomAlertProps, 'type'>> = (
  props
) => <Alert type="warning" {...props} />

export const ErrorAlert: React.FC<Omit<CustomAlertProps, 'type'>> = (props) => (
  <Alert type="error" {...props} />
)

export const NeutralAlert: React.FC<Omit<CustomAlertProps, 'type'>> = (
  props
) => <Alert type="neutral" {...props} />

export {
  InfoCircleOutlined as InfoIcon,
  CheckCircleOutlined as SuccessIcon,
  ExclamationCircleOutlined as WarningIcon,
  CloseCircleOutlined as ErrorIcon,
  CloseOutlined as CloseIcon,
}
